import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { StoreState, RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import MessagingAPIService, { AttachmentQuery } from "messaging/_api/messaging-api-service";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import Forum from "messaging/forums/forum";
import Session from "users/__session/session";

const initialState: StoreState<PaginatedResponse<MessageFileInfo>> = {
  entries: {},
  loading: {},
  error: {},
};

export const populateForumAttachments = createAsyncThunk(
  "messages/getAttachmentsByForum",
  async (
    {
      session,
      forum,
      query,
    }: {
      session: Session;
      forum: Forum;
      query: AttachmentQuery;
      append: boolean;
    },
    thunkAPI
  ) => {
    try {
      const apiService = new MessagingAPIService(session);
      return await apiService.getAttachmentsByForum(forum, query);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const attachmentsSlice = createSlice({
  name: "attachments",
  initialState,
  reducers: {
    addAttachment: (state, action: PayloadAction<{ forumId: Guid; attachment: MessageFileInfo }>) => {
      const { forumId, attachment } = action.payload;
      if (!forumId) return;

      let existingForumAttachments = state.entries[forumId.value];
      existingForumAttachments = existingForumAttachments.appendOrReplaceData([attachment]);
      state.entries[forumId.value] = existingForumAttachments;
    },
    removeAttachment: (state, action: PayloadAction<Guid>) => {
      for (const [forumId, forumAttachments] of Object.entries(state.entries)) {
        if (!forumAttachments) continue;
        state.entries[forumId] = forumAttachments.removeDataById(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateForumAttachments.pending, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      state.loading[forumId] = true;
      state.error[forumId] = null;
    });
    builder.addCase(populateForumAttachments.fulfilled, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      let existingForumAttachments = state.entries[forumId] ?? PaginatedResponse.empty();
      if (!existingForumAttachments.isEmpty && action.meta.arg.append) {
        existingForumAttachments = existingForumAttachments.appendOrReplaceData(
          action.payload.data,
          action.payload.headers,
          (a, b) => a.createdDate.diff(b.createdDate) ?? 0
        );
        state.entries[forumId] = existingForumAttachments;
      } else {
        state.entries[forumId] = action.payload;
      }
      state.loading[forumId] = false;
    });
    builder.addCase(populateForumAttachments.rejected, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      state.loading[forumId] = false;
      state.error[forumId] = action.error;
    });
  },
});

const selectEntries = (state: RootState) => state.attachments.entries;

const selectAttachmentsByForumId = createSelector(
  [selectEntries, (_: RootState, forumId?: Guid) => forumId?.value ?? ""],
  (entries, id) => entries[id] ?? []
);

export const { addAttachment, removeAttachment } = attachmentsSlice.actions;

export const getIsLoadingAttachmentsByForumId = (forumId?: Guid) => (state: RootState) => {
  if (!forumId) return false;
  return state.attachments?.loading[forumId.value];
};
export const getAttachmentsByForumId = (forumId?: Guid) => (state: RootState) =>
  selectAttachmentsByForumId(state, forumId);
export const getErrorLoadingAttachmentsByForumId = (forumId: Guid) => (state: RootState) =>
  state.attachments.error[forumId?.value ?? ""];

export default attachmentsSlice;
